import type { ExtractItemsResponse } from "../api/extractAI";
import type { CreateTakeawayRequest } from "../api/userTakeaways";

/** The slice of ReflectionContent the save actually needs — structural, so this
 *  lib stays import-free of the hooks layer (like reflectionDrip). */
export interface ReflectionCaptureContext {
  spaceId: string;
  framing: string;
  question: string | null;
  index: number;
}

const startOfLocalDay = (ts: number): number => {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/** Turn a reflection answer + its card context + an AI extraction (null when
 *  the AI is unavailable) into the takeaway create payload. Unlike the quick
 *  capture (where actions win ties), a reflection answer is an insight unless
 *  the AI found ONLY actions — answers to reflective questions ("I want to
 *  feel at home with somebody") read like goals but are not tasks. The
 *  question/framing travel as source metadata, never inside content. */
export function buildReflectionTakeaway(
  text: string,
  ctx: ReflectionCaptureContext,
  extracted: ExtractItemsResponse | null,
  now: number = Date.now()
): CreateTakeawayRequest {
  const actions = extracted?.actionItems ?? [];
  const insights = extracted?.insights ?? [];
  const type = actions.length > 0 && insights.length === 0 ? "action" : "insight";

  let dueDate: number | undefined;
  if (type === "action") {
    const today = startOfLocalDay(now);
    const suggested = actions[0]?.suggestedDueDate;
    // A past suggestion would land the item pre-overdue — clamp to today.
    dueDate = suggested !== undefined && suggested >= today ? suggested : today;
  }

  return {
    type,
    content: text,
    spaceIds: [ctx.spaceId],
    dueDate,
    source: {
      type: "reflection",
      ...(ctx.question ? { question: ctx.question } : {}),
      ...(ctx.framing ? { framing: ctx.framing } : {}),
      dayIndex: ctx.index,
    },
  };
}
